import { Ellipsis, EllipsisVertical } from "lucide-react";
import type { FC } from "react";
import { Link } from "react-router-dom";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { commonIconProps16 } from "@/utils/constants";
import { formatTestId } from "@/utils/formatTestId";

import type { MenuItemDropDown } from "./type";

interface DropDownMenuProps {
  isVertical?: boolean;
  menuList: MenuItemDropDown[];
  classes?: string;
  isAllSelected?: boolean;
  disabledTooltip?: string | null;
}

const DropDownMenu: FC<DropDownMenuProps> = ({
  isVertical = true,
  menuList,
  classes,
  isAllSelected = false,
  disabledTooltip,
}) => {
  return (
    <DropdownMenu modal={false}>
      <div className="relative group inline-flex self-end sm:self-auto">
        <DropdownMenuTrigger asChild disabled={isAllSelected}>
          <button
            className={cn(
              "cursor-pointer",
              classes,
              isAllSelected && "pointer-events-none opacity-50"
            )}
            data-testid="dropdown-menu-trigger"
          >
            {isVertical ? (
              <EllipsisVertical {...commonIconProps16} />
            ) : (
              <Ellipsis {...commonIconProps16} />
            )}
          </button>
        </DropdownMenuTrigger>

        {isAllSelected && disabledTooltip && (
          <div
            className={cn(
              "absolute bottom-full mb-2 hidden group-hover:flex",
              "z-[500] pointer-events-none flex-col items-center",
              "w-28 p-2 rounded-md bg-gray-800 text-xs text-white shadow-md text-center",
              "right-0 translate-x-0"
            )}
          >
            {disabledTooltip}
            <div className="absolute top-full right-2 border-4 border-transparent border-t-gray-800" />
          </div>
        )}
      </div>
      <DropdownMenuContent
        className="w-[160px]"
        align="start"
        sideOffset={5}
        alignOffset={-140}
        data-testid="dropdown-menu-content"
      >
        <DropdownMenuGroup>
          {menuList.map((elem, i) => (
            <>
              {elem.path && (
                <DropdownMenuItem key={i}>
                  <Link
                    to={elem.path}
                    state={{ from: elem.from }}
                    className="flex items-center gap-2 w-full"
                    data-testid={`dropdown-menu-item-${formatTestId(elem.title || "")}`}
                  >
                    {elem.svg}
                    <span className="text-xs">{elem.title}</span>
                  </Link>
                </DropdownMenuItem>
              )}
              {elem.onClick && (
                <DropdownMenuItem key={i} onSelect={elem.onClick}>
                  <div className="flex w-full items-center gap-2">
                    {elem.svg}
                    <span className="text-xs">{elem.title}</span>
                  </div>
                </DropdownMenuItem>
              )}
              {elem.dialog && elem.dialog}
            </>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default DropDownMenu;
